import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  FolderOpen,
  X
} from 'lucide-react';
import { getCategories, createCategory, updateCategory, deleteCategory } from '@/lib/api';
import { toast } from 'sonner';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: 'Users',
    image_url: '',
    order: 0,
    subcategories: []
  });

  const [newSubcategory, setNewSubcategory] = useState({ title: '', description: '' });

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Kategoriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      icon: 'Users',
      image_url: '',
      order: 0,
      subcategories: []
    });
    setNewSubcategory({ title: '', description: '' });
    setSelectedCategory(null);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setFormData({
      title: category.title,
      description: category.description,
      icon: category.icon,
      image_url: category.image_url,
      order: category.order,
      subcategories: category.subcategories || []
    });
    setDialogOpen(true);
  };

  const handleDelete = (category) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedCategory) return;
    
    try {
      await deleteCategory(selectedCategory.id);
      toast.success('Kategori başarıyla silindi');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Kategori silinirken hata oluştu');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedCategory) {
        await updateCategory(selectedCategory.id, formData);
        toast.success('Kategori başarıyla güncellendi');
      } else {
        await createCategory(formData);
        toast.success('Kategori başarıyla oluşturuldu');
      }
      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Kategori kaydedilirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const addSubcategory = () => {
    if (!newSubcategory.title) return;
    
    setFormData(prev => ({
      ...prev,
      subcategories: [
        ...prev.subcategories,
        {
          id: `new-${Date.now()}`,
          title: newSubcategory.title,
          description: newSubcategory.description,
          order: prev.subcategories.length
        }
      ]
    }));
    setNewSubcategory({ title: '', description: '' });
  };

  const removeSubcategory = (index) => {
    setFormData(prev => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== index)
    }));
  };

  const iconOptions = ['Users', 'Printer', 'Car', 'GraduationCap'];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-categories">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Kategoriler
            </h2>
            <p className="text-slate-600">Hizmet kategorilerini yönetin</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="add-category-btn"
            className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
          >
            <Plus className="w-4 h-4" />
            Yeni Kategori
          </Button>
        </div>

        {/* Categories List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
          </div>
        ) : categories.length > 0 ? (
          <div className="grid gap-4">
            {categories.map((category, index) => (
              <Card 
                key={category.id} 
                data-testid={`category-item-${index}`}
                className="border-slate-200"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {category.image_url ? (
                          <img 
                            src={category.image_url} 
                            alt={category.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FolderOpen className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-slate-800 mb-1">
                          {category.title}
                        </h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                          {category.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span>İkon: {category.icon}</span>
                          <span>Sıra: {category.order}</span>
                          <span>{category.subcategories?.length || 0} alt kategori</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`edit-category-${index}`}
                        onClick={() => handleEdit(category)}
                        className="gap-1"
                      >
                        <Pencil className="w-4 h-4" />
                        Düzenle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`delete-category-${index}`}
                        onClick={() => handleDelete(category)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Sil
                      </Button>
                    </div>
                  </div>

                  {/* Subcategories */}
                  {category.subcategories && category.subcategories.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <Accordion type="single" collapsible>
                        <AccordionItem value="subcategories" className="border-none">
                          <AccordionTrigger className="text-sm text-slate-600 hover:no-underline py-2">
                            Alt Kategoriler ({category.subcategories.length})
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="grid gap-2 pt-2">
                              {category.subcategories.map((sub, subIndex) => (
                                <div 
                                  key={sub.id}
                                  className="p-3 bg-slate-50 rounded-lg"
                                >
                                  <p className="font-medium text-slate-800 text-sm">
                                    {sub.title}
                                  </p>
                                  {sub.description && (
                                    <p className="text-xs text-slate-500 mt-1">
                                      {sub.description}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-20 text-center">
              <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Henüz kategori yok
              </h3>
              <p className="text-slate-600 mb-4">
                İlk kategorinizi ekleyerek başlayın
              </p>
              <Button 
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
              >
                <Plus className="w-4 h-4" />
                Kategori Ekle
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="category-dialog">
            <DialogHeader>
              <DialogTitle>
                {selectedCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Başlık *</Label>
                  <Input
                    id="title"
                    data-testid="category-title-input"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="icon">İkon</Label>
                  <select
                    id="icon"
                    data-testid="category-icon-select"
                    value={formData.icon}
                    onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  data-testid="category-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="image_url">Görsel URL</Label>
                  <Input
                    id="image_url"
                    data-testid="category-image-input"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="order">Sıra</Label>
                  <Input
                    id="order"
                    type="number"
                    data-testid="category-order-input"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>

              {/* Subcategories */}
              <div className="space-y-4">
                <Label>Alt Kategoriler</Label>
                
                {/* Add new subcategory */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Alt kategori adı"
                    data-testid="new-subcategory-title"
                    value={newSubcategory.title}
                    onChange={(e) => setNewSubcategory(prev => ({ ...prev, title: e.target.value }))}
                  />
                  <Input
                    placeholder="Açıklama"
                    data-testid="new-subcategory-description"
                    value={newSubcategory.description}
                    onChange={(e) => setNewSubcategory(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <Button 
                    type="button" 
                    onClick={addSubcategory}
                    data-testid="add-subcategory-btn"
                    variant="outline"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Subcategories list */}
                {formData.subcategories.length > 0 && (
                  <div className="space-y-2">
                    {formData.subcategories.map((sub, index) => (
                      <div 
                        key={sub.id}
                        className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">{sub.title}</p>
                          {sub.description && (
                            <p className="text-xs text-slate-500">{sub.description}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSubcategory(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                >
                  İptal
                </Button>
                <Button 
                  type="submit" 
                  data-testid="save-category-btn"
                  disabled={saving}
                  className="bg-[#005CB2] hover:bg-[#004B92] text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Kaydediliyor...
                    </>
                  ) : (
                    'Kaydet'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Kategoriyi Sil</AlertDialogTitle>
              <AlertDialogDescription>
                "{selectedCategory?.title}" kategorisini silmek istediğinizden emin misiniz? 
                Bu işlem geri alınamaz ve ilgili tüm alt kategoriler de silinecektir.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                data-testid="confirm-delete-btn"
                className="bg-red-600 hover:bg-red-700"
              >
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCategories;
